// Copyright (c) Docugami, Inc. All rights reserved.

import { addBindingThunk, addEntryThunk } from "components/reports/reportsActions";
import { ChunkInstance } from "networking/api";
import { useCallback } from "react";
import { selectAllEntries } from "store/reports/reportsSlice";
import { validateBinding } from "./ChunkLinking";
import { getChunkLabel } from "./LabelChunk";
import { useAppDispatch, useAppSelector } from "./redux";
import { selectEntries, selectReportEntryBindingsForCurrentDocument, selectReportId } from "store/reports/selectors";

export function useAddReportEntry(documentId: string) {
    const reportId = useAppSelector(selectReportId);
    const entriesState = useAppSelector(selectEntries);
    const documentBindings = useAppSelector(selectReportEntryBindingsForCurrentDocument);

    const dispatch = useAppDispatch();

    return useCallback(async (chunkInstance: ChunkInstance, label?: string, targetIndex?: number, groupId?: string): Promise<void> => {
        if (!entriesState || !documentBindings) {
            return;
        }

        if (!chunkInstance.spanId) {
            return;
        }

        const targetLabel = !!label ? label : dispatch(getChunkLabel(chunkInstance));

        const entries = selectAllEntries(entriesState);
        const existingEntry = entries.find(x => x.title.toLowerCase() === targetLabel.toLowerCase());

        const instanceId = chunkInstance.spanId;
        if (!!existingEntry) {
            if (!validateBinding(chunkInstance!, documentBindings.entities[existingEntry?.id ?? '']?.bindings)) {
                // There is an existing entry with the target label, but the new instance cannot be bound to it
                return Promise.reject(`There is an existing item with this label and the new instance cannot be added to it.`);
            }

            dispatch(addBindingThunk(reportId, {
                folderId: documentId,
                entryId: existingEntry.id,
                instanceId: instanceId
            }))
        }
        else {
            // New Entry
            dispatch(addEntryThunk(reportId, {
                folderId: documentId,
                instanceId: instanceId,
                label: targetLabel,
                index: targetIndex,
                groupId: groupId
            }));
        }
    }, [documentId, entriesState, documentBindings, dispatch, reportId])
}
